import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { useBookingForm } from "../../hooks/useBookingForm";
import { getGlamPackage } from "../../services/api";
import { BookingProgress } from "./booking-progress";
import { LocationStep } from "./location-step";
import { ScheduleStep } from "./schedule-step";
import { BookingSummary, SummaryPackage } from "./booking-summary";
import { BookingConfirmation } from "./booking-confirmation";

interface BookingFormProps {
  onBack: () => void;
  selectedPackageId: string;
}

const STEP_COPY: Record<string, { eyebrow: string; title: string; sub: string }> = {
  location: {
    eyebrow: "Step 1 of 2",
    title: "Tell us about you",
    sub: "A few details so we can prepare for your session.",
  },
  schedule: {
    eyebrow: "Step 2 of 2",
    title: "Choose your time & finish up",
    sub: "Pick a slot, add any final notes, and you're set.",
  },
};

export function BookingForm({ onBack, selectedPackageId }: BookingFormProps) {
  const {
    step,
    formData,
    updateFormData,
    image,
    setImage,
    errors,
    selectedDate,
    slots,
    slotsStatus,
    selectedSlot,
    selectDate,
    selectSlot,
    retrySlots,
    handleConsentChange,
    featuredImageConsent,
    customFacesCount,
    setCustomFacesCount,
    next,
    back,
    isSubmitting,
    submitError,
    showSuccess,
    confirmation,
  } = useBookingForm({ selectedPackageId });

  const [pkg, setPkg] = useState<SummaryPackage | null>(null);
  const [pkgLoading, setPkgLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setPkgLoading(true);
    getGlamPackage(selectedPackageId)
      .then((res: any) => {
        const d = res?.data?.data;
        if (active && d) {
          setPkg({
            title: d.title,
            price: d.price,
            image: d.media_url || undefined,
            description: d.description,
          });
        }
      })
      .catch(() => {
        /* summary degrades gracefully without package details */
      })
      .finally(() => active && setPkgLoading(false));
    return () => {
      active = false;
    };
  }, [selectedPackageId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const prevStep = () => {
    if (step === "schedule") back();
    else onBack();
  };

  // ── Success takes over the whole screen — a moment of resolution ──
  if (showSuccess && confirmation) {
    return (
      <BookingConfirmation
        confirmation={confirmation}
        pkg={pkg}
        glamSpot={formData.location as "studio" | "home" | undefined}
        homeAddress={formData.homeAddress}
        onHome={() => (window.location.href = "/")}
      />
    );
  }

  const copy = STEP_COPY[step];
  const primaryLabel = step === "schedule" ? "Confirm booking" : "Continue";

  const summary = (
    <BookingSummary
      pkg={pkg}
      pkgLoading={pkgLoading}
      formData={formData}
      customFacesCount={customFacesCount}
      featuredImageConsent={featuredImageConsent}
      selectedSlot={selectedSlot}
      selectedDate={selectedDate}
      step={step}
    />
  );

  const ActionButtons = () => (
    <div className="flex items-center justify-between gap-4">
      <button
        type="button"
        onClick={prevStep}
        disabled={isSubmitting}
        className="inline-flex items-center gap-2 rounded-full border border-[#E3C9C8] px-5 py-3 text-sm font-medium text-[#B87775] transition hover:border-[#B87775] hover:bg-[#FBEFEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2 disabled:opacity-50"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {step === "schedule" ? "Back" : "Packages"}
      </button>
      <button
        type="button"
        onClick={next}
        disabled={isSubmitting}
        className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#B87775] px-6 py-3 text-sm font-medium text-white transition hover:bg-[#a96764] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none"
      >
        {isSubmitting ? (
          <>
            <svg
              className="h-4 w-4 animate-spin text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Processing…
          </>
        ) : (
          <>
            {primaryLabel}
            {step !== "schedule" && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
          </>
        )}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FBEFEF]">
      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:py-12">
        {/* Header */}
        <div className="flex items-center justify-center">
          <img
            src="/images/bookASession.svg"
            alt="Book a session"
            className="w-[150px] md:w-[200px]"
          />
        </div>

        <div className="mx-auto mt-8 max-w-md">
          <BookingProgress step={step} />
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          {/* ── Form column ─────────────────────────────────── */}
          <main className="min-w-0">
            {/* Mobile collapsible summary */}
            <details className="group mb-6 overflow-hidden rounded-2xl border border-[#EAD7D6] bg-white lg:hidden">
              <summary className="flex cursor-pointer list-none items-center justify-between px-5 py-4 text-sm font-medium text-[#3a2e2c]">
                <span className="text-xs uppercase tracking-[0.2em] text-[#B87775]">
                  Your booking{" "}
                  {pkg && <span className="ml-1 text-[#9A6B69]">· ${pkg.price}</span>}
                </span>
                <ChevronDown
                  className="h-4 w-4 text-[#B87775] transition-transform group-open:rotate-180"
                  aria-hidden="true"
                />
              </summary>
              <div className="border-t border-[#EAD7D6] p-4">{summary}</div>
            </details>

            <div className="rounded-2xl border border-[#EAD7D6] bg-white p-5 md:p-8 lg:p-10">
              <header className="mb-8">
                <p className="text-xs uppercase tracking-[0.24em] text-[#B87775]">
                  {copy.eyebrow}
                </p>
                <h1 className="mt-2 font-serif text-3xl leading-tight text-[#3a2e2c] md:text-4xl">
                  {copy.title}
                </h1>
                <p className="mt-2 text-[#7c6c6a]">{copy.sub}</p>
                <p className="mt-3 text-sm text-[#9A6B69]">
                  Fields marked <span className="text-[#B87775]">*</span> are
                  required.
                </p>
              </header>

              <div key={step} className="animate-charme-slide">
                  {step === "location" && (
                    <LocationStep
                      location={formData.location as any}
                      onLocationChange={(value) => updateFormData({ location: value })}
                      homeAddress={formData.homeAddress as any}
                      onHomeAddressChange={(value) => updateFormData({ homeAddress: value })}
                      landmark={formData.landmark as any}
                      onLandmarkChange={(value) => updateFormData({ landmark: value })}
                      numberOfFaces={formData.numberOfFaces as any}
                      customFacesCount={customFacesCount}
                      onNumberOfFacesChange={(value) => {
                        updateFormData({ numberOfFaces: value });
                        if (value !== "more") setCustomFacesCount("");
                      }}
                      onCustomFacesChange={(value) => {
                        setCustomFacesCount(value);
                        updateFormData({ customFacesCount: value });
                      }}
                      error={errors.location}
                      homeAddressError={errors.homeAddress}
                      facesError={errors.numberOfFaces}
                      formData={formData}
                      onFieldChange={(field, value) => updateFormData({ [field]: value })}
                      errors={errors}
                    />
                  )}

                  {step === "schedule" && (
                    <ScheduleStep
                      formData={formData}
                      selectedDate={selectedDate}
                      slots={slots}
                      slotsStatus={slotsStatus}
                      selectedSlot={selectedSlot}
                      onDateSelect={selectDate}
                      onSlotSelect={selectSlot}
                      onRetrySlots={retrySlots}
                      featuredImageConsent={featuredImageConsent}
                      onConsentChange={handleConsentChange}
                      onFieldChange={(field, value) => updateFormData({ [field]: value })}
                      errors={errors}
                      onImageUpload={(file: File) => {
                        setImage(file);
                        updateFormData({ image: file });
                      }}
                      onImageRemove={() => {
                        setImage(null);
                        updateFormData({ image: null });
                      }}
                      image={image}
                    />
                  )}
              </div>

              {/* Form-level error (e.g. submit failure) */}
              <div aria-live="assertive">
                {submitError && (
                  <div
                    className="mt-6 rounded-xl border border-[#e6b9b7] bg-[#FBEFEF] px-4 py-3 text-sm text-[#a14f4c]"
                    role="alert"
                  >
                    {submitError}
                  </div>
                )}
              </div>

              {/*
                One action row, always in the document flow so it can never go
                missing. On mobile it sticks to the bottom of the viewport while
                the long form scrolls (the primary CTA stays reachable); on
                desktop it settles statically at the foot of the card.
              */}
              <div className="sticky bottom-0 z-10 mt-10 border-t border-[#EAD7D6] bg-white py-4 shadow-[0_-8px_24px_rgba(184,119,117,0.08)] lg:static lg:border-t-0 lg:bg-transparent lg:py-0 lg:shadow-none">
                <ActionButtons />
                <p className="pb-[env(safe-area-inset-bottom)]" aria-hidden="true" />
              </div>
            </div>
          </main>

          {/* ── Summary rail (desktop) ──────────────────────── */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">{summary}</div>
          </aside>
        </div>
      </div>
    </div>
  );
}
